import { z } from 'zod';
/**
 * Editable welcome / disclaimer panel — Linda feedback 2026-05-20.
 *
 * Single platform-wide markdown document that suppohrt_admin edits via
 * Beheer ⟶ Welkomstpaneel. Surfaced to every authenticated user as:
 *   1. A modal on first visit (and again whenever the version bumps).
 *   2. A dismissible banner on the /kennisagent page thereafter.
 *   3. A dedicated /help page that always renders the latest content.
 *
 * Storage:
 *   - Document body lives at `system_settings/welcome_panel` (SUPPOHRT) or
 *     `tenants/{tenantId}/settings/welcome_panel` for a tenant with its own
 *     panel (RHOGO, besluit 26 okt 2026).
 *   - Per-user dismissal at
 *     `tenants/{tenantId}/users/{userId}/preferences/welcome_panel`
 *     with `lastSeenVersion`. Modal triggers when version > lastSeenVersion.
 *
 * Why a version bump (not a single "I've seen it" boolean): when Linda
 * updates the disclaimer (e.g. new clinical caveat), every user should
 * re-see the modal once. Storing a monotonic version + per-user
 * lastSeenVersion gives "everyone sees the next change exactly once"
 * for free.
 */
/**
 * Stored document at system_settings/welcome_panel. The body is
 * markdown (rendered with react-markdown on the frontend); we allow up
 * to ~16K characters so Linda has room for a how-to + disclaimer + a
 * couple of clinical caveats without a hard limit being the design
 * pressure.
 */
export const WelcomePanel = z.object({
    content: z.string().max(16000),
    /** Tekst voor accounts zonder aiTools. Leeg = val terug op `content`,
     * zodat een bestaand paneel niet stilvalt na deze uitbreiding. */
    contentZonderAi: z.string().max(16000).default(''),
    version: z.number().int().nonnegative(),
    updatedAt: z.string().datetime({ offset: true }),
    updatedBy: z.string().min(1).max(200),
});
/**
 * Response shape for GET /api/v1/welcome-panel — combines the panel
 * body with the caller's per-user dismissal cursor so the frontend can
 * decide "show the modal" in a single round-trip.
 */
export const WelcomePanelResponse = z.object({
    content: z.string(),
    contentZonderAi: z.string().default(''),
    version: z.number().int().nonnegative(),
    updatedAt: z.string().datetime({ offset: true }),
    updatedBy: z.string(),
    lastSeenVersion: z.number().int().nonnegative(),
});
/**
 * Admin edit body. Bumping version is the backend's responsibility —
 * the editor can't pick a version manually because monotonicity is
 * load-bearing for "every user sees the new content once".
 */
export const WelcomePanelUpdateInput = z.object({
    content: z.string().max(16000),
    contentZonderAi: z.string().max(16000).default(''),
});
/**
 * Per-user dismissal. Frontend POSTs after the user clicks "Begrepen"
 * on the modal. Body carries the version that was dismissed so a race
 * (admin bumps mid-modal) doesn't accidentally suppress the new content.
 */
export const WelcomePanelDismissInput = z.object({
    version: z.number().int().nonnegative(),
});
/**
 * Welk paneel Beheer leest of opslaat. RHOGO heeft een eigen paneel
 * (`tenants/rhogo/settings/welcome_panel`); SUPPOHRT houdt het oorspronkelijke
 * platformdocument. Weglaten = het paneel van de eigen tenant. Alleen
 * suppohrt_admin mag een andere tenant opgeven.
 */
export const WelcomePanelTenant = z.enum(['suppohrt', 'rhogo']);
export const WelcomePanelQuery = z.object({
    tenant: WelcomePanelTenant.optional(),
});
//# sourceMappingURL=welcome-panel.js.map